import { useCallback } from 'react';
import type { NavigationProp } from '@react-navigation/native';
import { useNavigation } from '@react-navigation/native';

import type { PartialProduct, RecommendedItemFragment } from '@order/graphql';
import { telemetryProductFromInput, useTelemetry } from '@order/Telemetry';

import type { AppStackParamList } from '../../navigation/AppNavigation.props';
import { useDeliveryOrderInFlight } from '../useDeliveryOrderInFlight';

// ────────────────────────────────────────────────────────────────────────────────

export const useNavigateToPersonalizedProductDetails = (
  props: UseNavigateToPersonalizedProductDetailsProps,
) => {
  const { restaurantSlug, deliveryAddressId, recommendation } = props;
  const { name: recommendationName, ingredientIds } = recommendation;

  const { track } = useTelemetry();
  const { navigate } = useNavigation<NavigationProp<AppStackParamList>>();
  const { inFlightDeliveryOrderId, navigateToDeliveryOrderInFlightScreen } =
    useDeliveryOrderInFlight({ requestPolicy: 'cache-only' });

  // ─── HELPERS ─────────────────────────────────────────────────────

  const sendTelemetryEvent = useCallback(
    (product: PartialProduct) => {
      track('pdp.view', {
        entryPoint: 'menu',
        selectedProduct: telemetryProductFromInput(
          { ...product, location: restaurantSlug, name: recommendationName },
          undefined,
          { ...recommendation },
        ),
      });
    },
    [restaurantSlug, recommendation, recommendationName, track],
  );

  const navigateToProductDetails = useCallback(
    (productSlug: string) => {
      if (!restaurantSlug) return;

      const ingredientIdList = ingredientIds.join(',');

      navigate('Modal', {
        screen: 'ProductDetails',
        params: {
          productSlug,
          restaurantSlug,
          ingredientIds: ingredientIdList,
          name: recommendationName,
        },
      });
    },
    [navigate, restaurantSlug, ingredientIds, recommendationName],
  );

  const navigateToDeliveryProductDetails = useCallback(
    (productSlug: string) => {
      if (!deliveryAddressId) return;

      // Block delivery pdp navigation when there is an in-flight delivery order.
      if (inFlightDeliveryOrderId) {
        navigateToDeliveryOrderInFlightScreen();

        return;
      }

      const ingredientIdList = ingredientIds.join(',');

      navigate('Modal', {
        screen: 'DeliveryProductDetails',
        params: {
          productSlug,
          addressId: deliveryAddressId,
          ingredientIds: ingredientIdList,
          name: recommendationName,
        },
      });
    },
    [
      deliveryAddressId,
      inFlightDeliveryOrderId,
      ingredientIds,
      recommendationName,
      navigate,
      navigateToDeliveryOrderInFlightScreen,
    ],
  );

  // ─────────────────────────────────────────────────────────────────

  return useCallback(
    (product: PartialProduct) => {
      const { slug: productSlug = '' } = product;

      sendTelemetryEvent(product);

      if (deliveryAddressId) navigateToDeliveryProductDetails(productSlug);
      else navigateToProductDetails(productSlug);
    },
    [
      sendTelemetryEvent,
      deliveryAddressId,
      navigateToDeliveryProductDetails,
      navigateToProductDetails,
    ],
  );
};

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type UseNavigateToPersonalizedProductDetailsProps = Readonly<{
  restaurantSlug?: string;
  deliveryAddressId?: string;
  recommendation: RecommendedItemFragment;
}>;
